import { MergeOrUpdateStateAction, useMergeableState } from '@tableau/vizportalclient-common-components';
import {
  ButtonType,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  TabStyles,
} from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';

import { dialogBodyStyle } from '../../app/content/styles/ExternalContentActionsStyles';
import { Messages } from '../../codegen/Localize';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { CloseAndDismiss, ModalInstance, ReactModalOptions, ReactModalService } from '../../ts/services/ReactModalService';
import { cssClass } from '../../style';
import { TabDialogWidth } from './style/TabDialogWidth';

/**
 * Options for a React-based "action dialog", similar to the old Angular `IConfirmActionDialogOptions`.
 *
 * These options might be incomplete; not all the Angular functionality is implemented in React.
 * If you need more functionality, add it!
 */
export type ReactActionDialogOptions<P extends Object, S, TResult> = {
  /**
   * The title of the dialog, displayed in its title bar.
   * If title is empty or undefined, then the dialog won't have a title bar.
  */
  readonly title?: string;

  /** The main content of the dialog, where the user can configure the state data that gets passed into the action. */
  readonly bodyComponent: React.ComponentType<ReactActionDialogBodyProps<P, S>>;

  /** The initial value of the dialog's state data */
  readonly initialDialogState: S;

  /**
   * Additional static information passed to the dialog body component and to the action.
   * Despite being called "initial", these never change while the dialog is open.
   *
   * This prop may be used to avoid passing data to the dialog body and/or action function via closure,
   * thus allowing the those to be unit-tested more easily.
   */
  readonly initialBodyProps: P;

  /** The name of the single button that executes the action. The name might be dependent on the data from the dialog body. */
  readonly actionName: string | ((bodyProps: P, dialogState: S) => string);

  /**
   * The action to take when the user clicks the Confirm button. The bodyProps and dialogState are
   * passed in.
   *
   * - If the action is successful, the promise should be resolved;
   * - if the action is unsuccessful, the promise should be rejected.
   *
   * The dialog state can be updated from within the action, for example, to set an error message
   * in the dialog body if the action failed. This is useful only if `dismissDialogOnActionFailed` is false.
   */
  action: (bodyProps: P, dialogState: S, updateDialogState: React.Dispatch<MergeOrUpdateStateAction<S>>) => PromiseLike<TResult>;

  /**
   * If provided, it will check this function to see if the action button should be disabled
   * based on the user's selections.
   */
  actionDisabled?: (bodyProps: P, dialogState: S) => boolean;

  /**
   * How the dialog should behave if the action promise is rejected.
   * - If dismissDialogOnActionFailed is false, the dialog will stay open, which allows the user to
   *   either dismiss the dialog or select different options and try again.
   * - If dismissDialogOnActionFailed is true, the dialog will automatically dismiss.
   */
  dismissDialogOnActionFailed: boolean;

  /**
   * If true, display the action button as ButtonType.Destructive.
   * False by default.
   */
  isActionDelete?: boolean;

  /**
   * If true, the cancel and action buttons are centered in the dialog.
   * False by default.
   */
  centerActionButtons?: boolean;

  /**
   * The dialog width, use TabDialogWidth.width375px as a default
   */
  dialogWidth: TabDialogWidth;

  /**
   * Optionally pass in style to the dialog body. This is discouraged, as the Dialog Widget
   * is already implemented according to the Visual Design style guidelines.
   */
  dialogBodyStyle?: React.CSSProperties;
};

/**
 * Props for the client-specified dialog body content. The body is a controlled component, with the
 * dialog data being managed by its parent, so that this data can be passed into the action when the user clicks the button.
 */
export type ReactActionDialogBodyProps<P extends Object, S> = P & {
  /** True when the async action is in progress. Dialogs should disable their input affordances while the action is busy. */
  readonly actionBusy: boolean;

  readonly dialogState: S;

  /**
   * Updates the dialog state. Like the `setState` of class components, this will properly merge
   * partial state objects, or apply the given updater function.
   */
  readonly updateDialogState: React.Dispatch<MergeOrUpdateStateAction<S>>;

  /**
   * Performs the action, as if the user clicked the action button. This function is provided to the
   * body so that it can be bound to the enter keypress when the certain elements are focused (where
   * this behavior is desired).
   *
   * This function internally evaluates `actionDisabled`, and will do nothing if it evaluates to true.
   */
  readonly doAction: () => void;
};

/**
 * Opens an "action dialog". This is the most common dialog pattern, in which the user is presented with a
 * "Confirm" and "Cancel" button. The dialog also allows client-specified dialog content that lets the user
 * set up some data (the "dialog state") before confirming.
 *
 * ***For simple confirmation dialogs with only body text (no interactive elements except the Confirm/Cancel buttons),
 * use ReactConfirmationDialog.***
 *
 * Upon the user clicking "Confirm", the dialog will block (buttons disabled, dialog remains onscreen)
 * and the action will be performed. If the action is successful, the dialog then closes.
 * If the action is unsuccessful, the behavior depends on the value of the dismissDialogOnActionFailed option
 * (see documentation for the options).
 *
 * This function returns a `ModalInstance` that wraps the entire workflow in a promise:
 * - After successful completion of the action, the dialog closes and the ModalInstance promise is resolved.
 * - If the dialog gets dismissed (via clicking the close button, glass, or cancel button, or after an
 *   unsuccessful action when dismissDialogOnActionFailed is true), the promise gets rejected.
 *   Dismissing the dialog is a normal occurrence, so the calling code should swallow the rejection in most scenarios.
 *   If the dialog is being executed from a Redux saga, this swallowing happens automatically if the rejected promise
 *   propagates up to the root saga (i.e. you don't have to write your own `catch` block).
 */
export function openReactActionDialog<P extends Object, S, TResult>(
  options: ReactActionDialogOptions<P, S, TResult>
): ModalInstance<TResult> {
  return ReactModalService.openCustom(closeAndDismiss =>
    <ReactActionDialog {...options} closeAndDismiss={closeAndDismiss} />
  );
}

type ReactActionDialogProps<P extends Object, S, TResult> = ReactActionDialogOptions<P, S, TResult> & {
  closeAndDismiss: CloseAndDismiss<TResult>;
};

function ReactActionDialog<P extends Object, S, TResult>(props: ReactActionDialogProps<P, S, TResult>): JSX.Element {
  const {close: closeDialog, dismiss: dismissDialog } = props.closeAndDismiss;

  const bodyProps = props.initialBodyProps;
  const [dialogState, setDialogState] = useMergeableState(props.initialDialogState);
  const [busy, setBusy] = React.useState<boolean>(false);

  const doAction = React.useCallback(async () => {
    if (props.actionDisabled && props.actionDisabled(bodyProps, dialogState)) {
      return;
    }

    setBusy(true);
    try {
      const result = await props.action(bodyProps, dialogState, setDialogState);
      closeDialog(result);
    } catch {
      if (props.dismissDialogOnActionFailed) {
        dismissDialog('cancel');
      } else {
        setBusy(false);
      }
    }
  }, [setBusy, props.action, props.actionDisabled, closeDialog, bodyProps, dialogState]);

  const actionName = (typeof props.actionName === 'string') ? props.actionName : props.actionName(bodyProps, dialogState);

  const modalOptions: Omit<ReactModalOptions, 'dialogContent'> = {
    testId: ReactActionDialogTestId,
    title: props.title,
     type: (props.title && props.title.length > 0) ? DialogType.NormalWithTitleBar : DialogType.Uber,
    showCloseButton: false,
    dismissDialogOnCloseAction: true,
    shouldNotCloseOnGlassClick: busy,
    positioningBehavior: DialogPositioningBehavior.Centered,
    dialogWidth: props.dialogWidth,
    dialogBodyStyle: props.dialogBodyStyle ?? dialogBodyStyle,
    disableAutoFocus: false,
  };

  const dialogProps = ReactModalService.getDefaultDialogProps(modalOptions, props.closeAndDismiss);

  return (
    <DialogWidget {...dialogProps}>
      <props.bodyComponent {...bodyProps} dialogState={dialogState} updateDialogState={setDialogState} actionBusy={busy} doAction={doAction} />
      <div className={props.centerActionButtons ? ActionButtonsCenter : ActionButtons} >
        <ButtonWithSpinner
          buttonType={ButtonType.Outline}
          clicked={() => dismissDialog('cancel')}
          testId={ReactActionDialogCancelButtonTestId}
          title={Messages.cancel()}
          disabled={busy}
        />
        <span className="tb-margin-left-wide">
          <ButtonWithSpinner
            buttonType={props.isActionDelete ? ButtonType.Destructive : ButtonType.Primary}
            clicked={doAction}
            testId={ReactActionDialogConfirmButtonTestId}
            title={actionName}
            disabled={busy || (!_.isUndefined(props.actionDisabled) && props.actionDisabled(bodyProps, dialogState))}
            showSpinner={busy}
          />
        </span>
      </div>
    </DialogWidget>
  );
}

const actionButtonStyle: React.CSSProperties = {
  marginTop: TabStyles.Spacing.MediumSpace,
  whiteSpace: 'nowrap', // Prevent buttons from wrapping
  textAlign: 'right'
};

const ActionButtons = cssClass('confirm-action-dialog', 'action-buttons', actionButtonStyle);
const ActionButtonsCenter = cssClass('confirm-action-dialog', 'action-buttons-center', {
 ...actionButtonStyle,
 textAlign: 'center'
});

export const ReactActionDialogTestId = 'react-confirm-action-dialog';
export const ReactActionDialogCancelButtonTestId = 'confirm-action-dialog-cancel';
export const ReactActionDialogConfirmButtonTestId = 'confirm-action-dialog-confirm';
